'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  type GtmAddToCartEvent,
  type BaseEvent,
  type GtmBeginCheckoutEvent,
  type ConsentState,
  type EventParams,
  type GtmPageViewEvent,
  type GtmRemoveFromCartEvent,
  type GtmPurchaseEvent,
  type GtmViewCartEvent,
  type GtmSearchEvent,
  type GtmViewProductEvent,
  type GtmViewItemEvent,
  type GtmViewItemListEvent
} from '../typings'
import { useGTMContext } from './useGTMContext'

export function useGtmEvents() {
  const { dataLayerManager, consentManager, isInitialized, config } = useGTMContext()

  const [consentState, setConsentState] = useState<ConsentState | null>(null)

  useEffect(() => {
    if (!consentManager) {
      return
    }

    setConsentState(consentManager.getCurrentConsentState())

    consentManager.onConsentUpdate(setConsentState)

    return () => {
      consentManager.removeConsentUpdateCallback(setConsentState)
    }
  }, [consentManager])

  /**
   * Consent is deliberately NOT enforced here. A `dataLayer.push` stores nothing
   * on the device and sends nothing over the network, so it is not the act that
   * consent governs — the tag firing is. Enforcement lives downstream, in Google
   * Consent Mode and the per-tag consent checks of each container.
   *
   * Withholding the push additionally broke every CMP we do not install
   * ourselves, and discarded the pre-consent events Google Consent Mode replays
   * (basic) or models from (advanced).
   */
  const trackEvent = useCallback(
    <T extends BaseEvent>(name: T['event'], params: EventParams<T>) => {
      if (!isInitialized || !dataLayerManager) {
        return false
      }

      dataLayerManager.pushEvent<T>(name, params)

      return true
    },
    [isInitialized, dataLayerManager]
  )

  const trackPageView = useCallback(
    (params: EventParams<GtmPageViewEvent>) => trackEvent<GtmPageViewEvent>('page_view', params),
    [trackEvent]
  )

  const trackAddToCartEvent = useCallback(
    (params: EventParams<GtmAddToCartEvent>) =>
      trackEvent<GtmAddToCartEvent>('add_to_cart', params),
    [trackEvent]
  )

  const trackRemoveFromCartEvent = useCallback(
    (params: EventParams<GtmRemoveFromCartEvent>) =>
      trackEvent<GtmRemoveFromCartEvent>('remove_from_cart', params),
    [trackEvent]
  )

  const trackViewCartEvent = useCallback(
    (params: EventParams<GtmViewCartEvent>) => trackEvent<GtmViewCartEvent>('view_cart', params),
    [trackEvent]
  )

  const trackBeginCheckoutEvent = useCallback(
    (params: EventParams<GtmBeginCheckoutEvent>) =>
      trackEvent<GtmBeginCheckoutEvent>('begin_checkout', params),
    [trackEvent]
  )

  const trackPurchaseEvent = useCallback(
    (params: EventParams<GtmPurchaseEvent>) => trackEvent<GtmPurchaseEvent>('purchase', params),
    [trackEvent]
  )

  const trackViewItemEvent = useCallback(
    (params: EventParams<GtmViewItemEvent>) => trackEvent<GtmViewItemEvent>('view_item', params),
    [trackEvent]
  )

  const trackViewItemListEvent = useCallback(
    (params: EventParams<GtmViewItemListEvent>) =>
      trackEvent<GtmViewItemListEvent>('view_item_list', params),
    [trackEvent]
  )

  const trackSearchEvent = useCallback(
    (params: EventParams<GtmSearchEvent>) => trackEvent<GtmSearchEvent>('search', params),
    [trackEvent]
  )

  const trackViewProductEvent = useCallback(
    (params: EventParams<GtmViewProductEvent>) =>
      trackEvent<GtmViewProductEvent>('view_product', params),
    [trackEvent]
  )

  return {
    trackEvent,

    // Ecommerce events
    trackAddToCartEvent,
    trackRemoveFromCartEvent,
    trackViewCartEvent,
    trackBeginCheckoutEvent,
    trackPurchaseEvent,
    trackViewItemEvent,
    trackViewItemListEvent,

    trackPageView,
    trackSearchEvent,
    trackViewProductEvent,

    isInitialized,
    dataLayerManager,
    consentManager,
    config,
    consentState
  }
}
